import { useState } from 'react';
import { useUpdateDuration } from '@/hooks/useInterventionMutations';
import { formatDurationMinutes } from '@/lib/dates';

type Props = {
  interventionId: string;
  currentDurationMinutes: number;
  onClose: () => void;
};

const PRESETS = [
  { delta: 15, label: '+15m' },
  { delta: 30, label: '+30m' },
  { delta: 60, label: '+1h' },
];

export default function DurationPopover({ interventionId, currentDurationMinutes, onClose }: Props) {
  const [customMinutes, setCustomMinutes] = useState(currentDurationMinutes);
  const updateDuration = useUpdateDuration();

  async function apply(newMinutes: number) {
    if (newMinutes <= 0 || newMinutes % 15 !== 0) return;
    try {
      await updateDuration.mutateAsync({ id: interventionId, dureeEstimeeMinutes: newMinutes });
      onClose();
    } catch {
      // err state shown via mutation
    }
  }

  return (
    <div className="bg-surface-white border border-border-light rounded-lg shadow-lg p-4 w-72">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-text-primary mb-3">
        Modifier la durée
      </h3>

      <div className="text-xs text-text-muted mb-2">
        Durée actuelle :{' '}
        <span className="font-mono font-semibold text-text-primary">
          {formatDurationMinutes(currentDurationMinutes)}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {PRESETS.map((p) => (
          <button
            key={p.delta}
            type="button"
            onClick={() => apply(currentDurationMinutes + p.delta)}
            disabled={updateDuration.isPending}
            className="btn btn-secondary btn-sm justify-center"
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="space-y-1 mb-3">
        <label htmlFor="custom-duration" className="input-label">
          Durée personnalisée (min)
        </label>
        <input
          id="custom-duration"
          type="number"
          min={15}
          step={15}
          value={customMinutes}
          onChange={(e) => setCustomMinutes(Number(e.target.value))}
          className="input"
        />
      </div>

      {updateDuration.isError && (
        <div className="mb-3 px-3 py-2 rounded-sm bg-status-annule-bg text-status-annule-text text-xs">
          Erreur lors de la modification
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">
          Annuler
        </button>
        <button
          type="button"
          onClick={() => apply(customMinutes)}
          disabled={updateDuration.isPending}
          className="btn btn-primary btn-sm"
        >
          {updateDuration.isPending ? '…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}
